/**
 * Copyright © 2026 Adnuntius AS.
 */
import {computed, signal} from '@angular/core';
import {firstValueFrom} from 'rxjs';

import {
  SignalAdminResourceApi,
  SignalAdminResourceModel
} from './signal-admin-resource-api';
import {AdminResourceCopyConfig} from './admin-resource-copy-config';
import {createNotesSaveTarget} from '../directives/notes/notes-save-target';
import type {NotesSaveTarget} from '../directives/notes/notes-save-target';

export interface SignalAdminResourceTitleOptions {
  objectTypeKey: string;
  crumbParams: any[];
}

export class SignalAdminResourcePageState<T extends SignalAdminResourceModel> {
  readonly model = signal<T>(null);
  readonly isNew = signal(false);
  readonly editingRevision = signal(0);
  readonly editingReplacementDirty = signal(false);
  readonly panelFull = signal(false);
  readonly relatedData = signal<Record<string, any>>({});
  private readonly permissionReadOnly = signal(false);
  private readonly sessionReadOnly = signal(false);
  readonly readOnly = computed(() => this.permissionReadOnly() || this.sessionReadOnly());

  constructor(
    model: T,
    isNew: boolean,
    readOnly: boolean,
    readonly api: SignalAdminResourceApi<T>,
    readonly objectType: string,
    readonly nameProperty = 'name',
    readonly copyConfig?: AdminResourceCopyConfig,
    readonly blockObjectStateChanges?: string,
    readonly titleOptions?: SignalAdminResourceTitleOptions,
    readonly confirmation?: {warningMessage?: string; object: string}
  ) {
    this.model.set(model);
    this.isNew.set(isNew);
    this.permissionReadOnly.set(readOnly);
  }

  setReadOnly(readOnly: boolean): void {
    this.permissionReadOnly.set(readOnly);
  }

  forceReadOnly(): void {
    this.sessionReadOnly.set(true);
  }

  update(model: T): void {
    this.model.set(model);
    this.isNew.set(false);
  }

  /** Publish in-page edits to title/context consumers without changing create state. */
  syncModel(model: T): void {
    this.model.set(model);
  }

  setPanelFull(full: boolean): void {
    this.panelFull.set(full);
  }

  updateRelatedData(type: string, data: any): void {
    this.relatedData.update((current) => ({...current, [type]: data}));
  }

  /** Replace the form model from an explicit page action such as copying a template. */
  replaceForEditing(model: T, dirty = true): void {
    this.model.set(model);
    this.editingReplacementDirty.set(dirty);
    this.editingRevision.update((revision) => revision + 1);
  }

  /**
   * `adn-notes` saveObject: partially saves the linked notes (as the old resource
   * savePartial did) and folds them back into page state so form saves keep them.
   */
  notesSaveTarget(param = 'notes'): NotesSaveTarget {
    const state = this;
    return createNotesSaveTarget({
      model: () => state.model(),
      readOnly: () => state.readOnly(),
      save: (payload, saveParam) => firstValueFrom(state.api.savePartial(payload, saveParam)),
      onSaved: (saveParam, links) => {
        state.model.set({...state.model(), [saveParam]: links});
      }
    }, param);
  }

  updateId(id: string): void {
    this.model.update((model) => ({...model, id}));
  }

  displayName(): string {
    return (this.model() as any)?.[this.nameProperty] || 'New';
  }
}
